// Hook per notifiche: fetch iniziale + subscribe realtime
import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotificationsStore, type AppNotification } from '../stores/notifications.store'
import api from '../lib/api'

// Shape reale della risposta GET /api/notifications
interface NotificationsResponse {
  items: AppNotification[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export function useNotifications() {
  const { setNotifications, markAsRead, markAllAsRead, unreadCount, isOpen, setOpen } =
    useNotificationsStore()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get<NotificationsResponse>('/notifications')
      // Il backend ritorna { items, page, pageSize, total, totalPages }
      // Estraiamo solo l'array items con fallback difensivo
      const payload = response.data
      return Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : []
    },
    refetchInterval: 60 * 1000, // Rifetch ogni minuto
  })

  // Sincronizza query → store
  useEffect(() => {
    if (query.data) {
      setNotifications(query.data)
    }
  }, [query.data, setNotifications])

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/notifications/${id}/read`)
      return id
    },
    onSuccess: (id) => {
      markAsRead(id)
      // Niente invalidateQueries: update ottimistico sufficiente, 60s poll sincronizza
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await api.post('/notifications/read-all')
    },
    onSuccess: () => {
      markAllAsRead()
    },
  })

  return {
    unreadCount,
    isOpen,
    setOpen,
    markAsRead: markReadMutation.mutate,
    markAllAsRead: markAllReadMutation.mutate,
    isLoading: query.isLoading,
  }
}
